import type { MessageInstance } from '@k-ucap/shared';

export interface MessageEncoder {
  readonly name: string;
  readonly standard: string;
  encode(instance: MessageInstance): Uint8Array;
  decode(bytes: Uint8Array): Partial<MessageInstance>;
}

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

export class PreviewMessageEncoder implements MessageEncoder {
  readonly name = 'PreviewMessageEncoder';
  readonly standard = 'POC_PREVIEW';

  encode(instance: MessageInstance): Uint8Array {
    const header = new Uint8Array(8);
    const view = new DataView(header.buffer);
    view.setUint16(0, instance.messageType, false);
    const seq = instance.sequenceNumber ?? 0;
    view.setUint32(2, seq >>> 0, false);

    const payloadJson = JSON.stringify({
      standard: instance.standard,
      name: instance.name,
      source: instance.source,
      target: instance.target,
      direction: instance.direction,
      timestamp: instance.timestamp,
      fields: instance.fields,
    });
    const payloadBytes = textEncoder.encode(payloadJson);
    view.setUint16(6, payloadBytes.length & 0xffff, false);

    const result = new Uint8Array(header.length + payloadBytes.length);
    result.set(header, 0);
    result.set(payloadBytes, header.length);
    return result;
  }

  decode(bytes: Uint8Array): Partial<MessageInstance> {
    if (bytes.length < 8) return {};
    const view = new DataView(bytes.buffer, bytes.byteOffset, 8);
    const messageType = view.getUint16(0, false);
    const sequenceNumber = view.getUint32(2, false);
    const payloadLength = view.getUint16(6, false);
    const payloadJson = textDecoder.decode(bytes.slice(8, 8 + payloadLength));
    let payload: Record<string, unknown> = {};
    try {
      payload = JSON.parse(payloadJson) as Record<string, unknown>;
    } catch {
      // ignore
    }
    return {
      messageType,
      sequenceNumber,
      ...payload,
    };
  }
}

export const createPreviewMessageEncoder = (): PreviewMessageEncoder => new PreviewMessageEncoder();

export const bytesToHex = (bytes: Uint8Array): string =>
  Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
    .join(' ');
